import { useState } from "react";
import { format, parseISO } from "date-fns";
import { ja } from "date-fns/locale";
import { useCalendar } from "../../hooks/useApi";
import type { CalendarEventResponse } from "../../api/types";
import { ImpactBadge } from "../../components/Badge";
import { LoadingSpinner } from "../../components/LoadingSpinner";

type Week = "current" | "next";
type Country = "US" | "JP" | "ALL";

export function CalendarPage() {
  const [week, setWeek] = useState<Week>("current");
  const [country, setCountry] = useState<Country>("ALL");

  const { data, isLoading, isError } = useCalendar(
    week,
    country === "ALL" ? undefined : country
  );

  const grouped = groupByDate(data?.events ?? []);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex-1">経済カレンダー</h1>
        <div className="flex gap-2">
          {(["current", "next"] as Week[]).map((w) => (
            <button
              key={w}
              onClick={() => setWeek(w)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                week === w
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {w === "current" ? "今週" : "来週"}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {(["ALL", "US", "JP"] as Country[]).map((c) => (
            <button
              key={c}
              onClick={() => setCountry(c)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                country === c
                  ? "bg-gray-800 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {data && (
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span>
            {format(parseISO(data.week_start), "M/d", { locale: ja })} –{" "}
            {format(parseISO(data.week_end), "M/d（EEE）", { locale: ja })}
          </span>
          <span className="bg-gray-100 px-2 py-0.5 rounded text-xs">
            {data.total_events}件
          </span>
          {data.high_impact_count > 0 && (
            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">
              HIGH: {data.high_impact_count}件
            </span>
          )}
        </div>
      )}

      {isLoading && <LoadingSpinner />}
      {isError && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 py-12 text-center text-gray-500 text-sm">
          経済カレンダーは現在ご利用いただけません（データ提供元の無料プランの制約）。
        </div>
      )}

      {!isLoading && data?.events.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 py-12 text-center text-gray-500 text-sm">
          この週の発表予定はありません
        </div>
      )}

      {Object.entries(grouped).map(([dateStr, events]) => (
        <section key={dateStr}>
          <h2 className="text-sm font-semibold text-gray-500 mb-2">
            {format(parseISO(dateStr), "M月d日（EEE）", { locale: ja })}
          </h2>
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {events.map((e, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <span className="text-xs text-gray-400 w-10 shrink-0">
                  {format(parseISO(e.release_time), "HH:mm")}
                </span>
                <span className="text-xs font-bold text-gray-500 w-6 shrink-0">{e.country}</span>
                <ImpactBadge level={e.impact.toUpperCase()} />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-900">{e.event}</span>
                  {e.unit && <span className="text-xs text-gray-400 ml-1">({e.unit})</span>}
                </div>
                <div className="text-right text-xs text-gray-500 space-x-3">
                  {e.actual != null && (
                    <span className="font-semibold text-gray-800">実績 {e.actual}</span>
                  )}
                  {e.estimate != null && <span>予想 {e.estimate}</span>}
                  {e.previous != null && <span>前回 {e.previous}</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function groupByDate(events: CalendarEventResponse[]): Record<string, CalendarEventResponse[]> {
  const result: Record<string, CalendarEventResponse[]> = {};
  for (const e of events) {
    const day = e.release_time.slice(0, 10);
    if (!result[day]) result[day] = [];
    result[day].push(e);
  }
  return result;
}
